import { useEffect, useState } from 'react';
import { ModalFooter, ModalBody, ModalHeader } from 'components/Modal';
import { useConfig, usePhrases } from 'contexts/ConfigContext';
import { getLoyaltyLinkTitle } from 'utils';
import { Field } from 'types/models/Forms';
import {
  FieldValues,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { Button } from 'react-bootstrap';
import { useLoyaltyRewards } from 'contexts/LoyaltyRewardContext';
import { ContextualLoader } from 'components/ContextualLoader';
import { FormGenerator } from 'core/components/RHF';
import { useSignUp } from 'hooks/useSignUp';
import Loader from 'react-spinners/BarLoader';
import { useGlobalUser } from 'contexts/UserContext';

interface VerifyLoyaltyProps {
  closeModal: () => void;
}

export const VerifyLoyaltyModalBody: React.FC<VerifyLoyaltyProps> = ({
  closeModal,
}) => {
  const { user } = useGlobalUser();
  const { verifyUserLoyalty, isVerifying, resendPin } = useLoyaltyRewards();
  const {
    loyalty: { cardPhrase, linkButtonText, linkTitleText },
  } = usePhrases();
  const methods = useForm();
  const loyaltyCard = user.loyalty_card;
  const { formData, isLoadingForms } = useSignUp();
  const { showResetLoyaltyPin } = useConfig();
  const [isResendDisabled, setIsResendDisabled] = useState(false);

  const setCardNumberDisabledWLA = (formFields: Field[]): Field[] => {
    const sanitisedFormData = formFields;
    formFields.forEach((field) => {
      if (field.name === 'loyalty_card') {
        field.disabled = true;
      }
    });
    return sanitisedFormData;
  };

  const handleVerify: SubmitHandler<FieldValues> = (formData) => {
    verifyUserLoyalty(formData);
  };

  const handleResendPin = () => {
    setIsResendDisabled(true);
    resendPin(loyaltyCard);
    setTimeout(() => {
      setIsResendDisabled(false);
    }, 20000);
  };

  useEffect(() => {
    methods.setValue('loyalty_card', loyaltyCard.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoadingForms || !formData) {
    return <Loader />;
  }

  return (
    <>
      <ModalHeader closeButton onRequestClose={closeModal} showCloseOnDesktop>
        <h2 className="title">
          {getLoyaltyLinkTitle(linkTitleText, cardPhrase)}
        </h2>
      </ModalHeader>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleVerify)}
          className="loyalty-verify-form"
          data-testid="verify-loyalty-form"
        >
          <ModalBody>
            {formData?.linkLoyaltyAccount &&
            formData?.linkLoyaltyAccount.length > 0 ? (
              <div className="col-sm-12">
                <FormGenerator
                  definition={setCardNumberDisabledWLA(
                    formData.linkLoyaltyAccount,
                  )}
                />
              </div>
            ) : (
              <div>Sorry, something has gone wrong. Please try again.</div>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              className="btn btn-primary"
              data-testid="verify-loyalty-btn"
              bsStyle="primary"
              type="submit"
              onClick={methods.handleSubmit(handleVerify)}
            >
              {linkButtonText}
            </Button>
            {showResetLoyaltyPin && (
              <Button
                className="btn btn-default resend-pin-btn"
                onClick={handleResendPin}
                disabled={isResendDisabled}
              >
                Resend PIN
              </Button>
            )}
          </ModalFooter>
        </form>
      </FormProvider>
      <ContextualLoader isVisible={isVerifying} />
    </>
  );
};
